import React, { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { logSecurityEvent } from '@/lib/security';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAdmin = false }) => {
  const { user, userProfile, loading, isAdmin } = useAuth();

  useEffect(() => {
    if (!loading && user) {
      logSecurityEvent('protected_route_access', {
        userId: user.id,
        requireAdmin,
        isAdmin,
        route: window.location.pathname
      }, 'low');
    }
  }, [user, loading, requireAdmin, isAdmin]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    logSecurityEvent('unauthorized_access_attempt', {
      route: window.location.pathname,
      requireAdmin
    }, 'medium');
    return <Navigate to="/auth" replace />;
  }

  if (requireAdmin && !isAdmin) {
    logSecurityEvent('admin_access_denied', {
      userId: user.id,
      route: window.location.pathname
    }, 'high');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive mb-2">Access Denied</h1>
          <p className="text-muted-foreground">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;